<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Chart as ChartJS, ChartArea } from 'chart.js'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chart from '@uiKit/components/Chart.vue'
import AppTabs from '@uiKit/components/Tabs.vue'
import Card from '@uiKit/components/Card.vue'
import type { AppTab } from '@uiKit/components/Tabs.vue'
import type { PortfolioGraphRental, PortfolioGraphValue } from '../types/portfolioTypes'

interface GradientContext {
  chart: ChartJS & { chartArea: ChartArea; ctx: CanvasRenderingContext2D }
}

interface TooltipContext {
  parsed: { y: number }
  label: string
}

type ChartMode = 'value' | 'rental'

interface Props {
  valueData?: PortfolioGraphValue | null
  rentalData?: PortfolioGraphRental | null
}

const props = withDefaults(defineProps<Props>(), {
  valueData: null,
  rentalData: null,
})

const activeMode = ref<ChartMode>('value')

const chartTabs: AppTab[] = [
  { key: 'value', label: 'Value' },
  { key: 'rental', label: 'Rental' },
]

const chartTitle = computed(() =>
  activeMode.value === 'value' ? 'Portfolio Value' : 'Monthly Rental',
)

const chartType = computed(() => (activeMode.value === 'value' ? 'line' : 'bar'))

function formatMonth(ym: string): string {
  const [yearStr, monthStr] = ym.split('-')
  const year = Number(yearStr)
  const monthIndex = Number(monthStr) - 1
  return new Date(year, monthIndex, 1).toLocaleString('en', { month: 'short' })
}

const valueLabels = computed(() =>
  (props.valueData?.series ?? []).map((point) => formatMonth(point.month)),
)

const rentalLabels = computed(() =>
  (props.rentalData?.series ?? []).map((point) => formatMonth(point.month)),
)

const portfolioValues = computed(() =>
  (props.valueData?.series ?? []).map((point) => point.portfolioValue),
)

const investedValues = computed(() =>
  (props.valueData?.series ?? []).map((point) => point.investedAmount),
)

const rentalValues = computed(() =>
  (props.rentalData?.series ?? []).map((point) => point.rentalIncome),
)

const chartData = computed(() => {
  if (activeMode.value === 'rental') {
    return {
      labels: rentalLabels.value,
      datasets: [
        {
          label: 'Monthly Rental Income',
          data: rentalValues.value,
          backgroundColor: (context: GradientContext) => {
            const { ctx, chartArea } = context.chart
            if (!chartArea) return '#12b76a'
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
            gradient.addColorStop(0, '#d1fadf')
            gradient.addColorStop(1, '#12b76a')
            return gradient
          },
          borderColor: 'transparent',
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    }
  }

  return {
    labels: valueLabels.value,
    datasets: [
      {
        label: 'Portfolio Value',
        data: portfolioValues.value,
        borderColor: '#7e79ff',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        backgroundColor: (context: GradientContext) => {
          const { ctx, chartArea } = context.chart
          if (!chartArea) return 'transparent'
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
          gradient.addColorStop(0, 'rgba(126, 121, 255, 0.18)')
          gradient.addColorStop(1, 'rgba(126, 121, 255, 0.0)')
          return gradient
        },
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#7e79ff',
        pointHoverBorderWidth: 2.5,
      },
      {
        label: 'Invested',
        data: investedValues.value,
        borderColor: '#a4a7ae',
        borderWidth: 1.5,
        borderDash: [5, 4],
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#a4a7ae',
        pointHoverBorderWidth: 2,
      },
    ],
  }
})

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#ffffff',
      borderColor: '#e9eaeb',
      borderWidth: 1,
      titleColor: '#717680',
      bodyColor: '#181d27',
      bodyFont: { weight: 'bold', size: 13 },
      titleFont: { size: 11 },
      padding: 10,
      cornerRadius: 10,
      callbacks: {
        label: (context: TooltipContext) => ` AED ${context.parsed.y.toLocaleString()}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: '#a4a7ae',
        font: { size: 11 },
        letterSpacing: -0.5,
      },
    },
    y: {
      beginAtZero: activeMode.value === 'rental',
      border: { display: false },
      grid: { color: '#f3f4f6' },
      ticks: {
        color: '#a4a7ae',
        font: { size: 11 },
        letterSpacing: -0.5,
        callback: (raw: number | string) => {
          const numeric = Number(raw)
          if (numeric >= 1000) return `${numeric / 1000}K`
          return String(numeric)
        },
      },
    },
  },
}))
</script>

<template>
  <Card variant="outlined" padding="0" direction="col" class="pvc">
    <!-- Header -->
    <Box align="center" justify="between" class="pvc__header">
      <Text type="page-title" is="span" class="pvc__title">{{ chartTitle }}</Text>
      <AppTabs v-model="activeMode" :tabs="chartTabs" size="sm" />
    </Box>

    <!-- Chart -->
    <Box class="pvc__chart">
      <Chart :type="chartType" :data="chartData" :chart-options="chartOptions" height="280px" />
    </Box>

    <!-- Legend -->
    <Box align="center" justify="center" gap="4" class="pvc__legend">
      <template v-if="activeMode === 'value'">
        <Box align="center" gap="2">
          <Box class="pvc__legend-dot pvc__legend-dot--value" />
          <Text type="caption" weight="medium" color="default" is="span" class="pvc__legend-label">
            Portfolio Value
          </Text>
        </Box>
        <Box align="center" gap="2">
          <Box class="pvc__legend-dot pvc__legend-dot--invested" />
          <Text type="caption" weight="medium" color="default" is="span" class="pvc__legend-label">
            Invested
          </Text>
        </Box>
      </template>
      <template v-else>
        <Box align="center" gap="2">
          <Box class="pvc__legend-dot pvc__legend-dot--rental" />
          <Text type="caption" weight="medium" color="default" is="span" class="pvc__legend-label">
            Monthly Rental Income
          </Text>
        </Box>
      </template>
    </Box>
  </Card>
</template>

<style scoped>
.pvc {
  overflow: hidden;
  width: 100%;
}

.pvc__header {
  padding: 1.125rem 1.25rem 0.75rem;
}

.pvc__title {
  letter-spacing: 0.1em;
}

.pvc__chart {
  padding: 0 0.75rem;
}

.pvc__legend {
  padding: 0.75rem 1.25rem 1rem;
}

.pvc__legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50px;
  flex-shrink: 0;
}

.pvc__legend-dot--value {
  background: #7e79ff;
}

.pvc__legend-dot--invested {
  background: var(--p-text-muted-color);
}

.pvc__legend-dot--rental {
  background: #12b76a;
}

.pvc__legend-label {
  white-space: nowrap;
}

@media (max-width: 992px) {
  .pvc__header {
    padding: 0.875rem 1rem 0.625rem;
    flex-wrap: wrap;
    gap: 0.625rem;
  }

  .pvc__title {
    font-size: 15px;
  }

  .pvc__chart {
    padding: 0 0.5rem;
  }

  .pvc__chart :deep(canvas) {
    max-height: 220px !important;
  }

  .pvc__legend {
    padding: 0.625rem 1rem 0.75rem;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .pvc__legend-label {
    white-space: normal;
  }
}
</style>
